import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { tendApi, TacticTemplateDefaultResolution, OverlayTemplateResponse } from '../../api/tendApi';
import { Logo } from '../../api/logoApi';
import { useBrand } from '../../contexts/BrandContext';
import { createPlaceholderContentItem } from './templatePlaceholders';
import TemplateDefaultSlot, { TemplateDefaultSlotBadge } from './TemplateDefaultSlot';

/**
 * The per-tactic default template slot (DEV: Tactic Default Template).
 *
 * Wraps TemplateDefaultSlot for one tactic: reads the resolution from
 * /tendv2/overlay-template-defaults/tactic, shows the template in effect
 * even when the tactic has none of its own (badged INHERITED · <source>),
 * and saves on pick like the Design ▾ slots. The first picker row, "None",
 * clears the scope's own row so the tactic inherits again. Preview content
 * is the placeholder copy for the tactic's post type (D3 — no real runs).
 *
 * Follows the host's Brand / Universal scope: Universal edits the global
 * tactic row and only sees global rows.
 */

type EditMode = 'brand' | 'universal';

const NEUTRAL_PREVIEW_FILL = '#101014';

const POST_TYPE_PLACEHOLDER: Record<string, string> = {
  single: 'post',
  post: 'post',
  video: 'video',
  carousel: 'carousel_body',
};

export interface TacticTemplateSlotProps {
  platform: string;
  tacticId: string;
  /** Tactic frontmatter post_type ('single' | 'video' | 'carousel'); picks the inherited slot. */
  postType: string;
  /** Tactic frontmatter take switch — a single-post take inherits the Video default. */
  take?: boolean;
  editMode: EditMode;
  canEdit: boolean;
  width?: number;
  label?: string;
}

const TacticTemplateSlot: React.FC<TacticTemplateSlotProps> = ({
  platform,
  tacticId,
  postType,
  take = false,
  editMode,
  canEdit,
  width = 150,
  label = 'Template',
}) => {
  const { currentBrand } = useBrand();
  const brandId = currentBrand ? Number(currentBrand.id) : null;
  const brandName = currentBrand?.name || '';
  const isUniversal = editMode === 'universal';
  const scopeType: 'global' | 'brand' = isUniversal ? 'global' : 'brand';

  const [resolution, setResolution] = useState<TacticTemplateDefaultResolution | null>(null);
  const [templates, setTemplates] = useState<OverlayTemplateResponse[]>([]);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewFillColor, setPreviewFillColor] = useState<string>(NEUTRAL_PREVIEW_FILL);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const brandLogo: Logo | null = useMemo(() => {
    const dl = currentBrand?.default_logo;
    if (!dl) return null;
    return {
      id: dl.id,
      logoKey: dl.logo_key,
      displayName: dl.display_name,
      url: dl.url,
      isDefault: dl.is_default,
      displayOrder: dl.display_order,
    };
  }, [currentBrand?.default_logo]);

  // The slot is reused across tactics (one instance, props change). Every
  // response is stamped with the request's key and dropped if the slot has
  // moved on, so a slow load or save for tactic A never lands under B.
  const requestKey = `${brandId}:${platform}:${tacticId}:${editMode}:${postType}:${take ? 'take' : ''}`;
  const keyRef = useRef(requestKey);
  keyRef.current = requestKey;

  useEffect(() => {
    const key = requestKey;
    if (!brandId || !platform || !tacticId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [res, tpls, settings] = await Promise.all([
          tendApi.getTacticTemplateDefault(platform, tacticId, isUniversal ? null : brandId, postType, take),
          tendApi.getOverlayTemplatesForModal(brandId, { scope: isUniversal ? 'global' : undefined }),
          tendApi.getBrandOverlaySettings(brandId),
        ]);
        if (keyRef.current !== key) return;
        setTemplates(tpls ?? []);
        setPreviewImageUrl(settings?.template_preview_image_url ?? settings?.universal_template_preview_image_url ?? null);
        setPreviewFillColor(settings?.background_color || NEUTRAL_PREVIEW_FILL);
        // A failed GET must not read as "nothing set" — the editor would
        // pick a template to "fix" it and overwrite a choice never shown.
        setResolution(res);
        setError(res ? null : 'Could not load');
      } catch (e) {
        if (keyRef.current !== key) return;
        console.error('Failed to load tactic template default:', e);
        setResolution(null);
        setError('Could not load');
      } finally {
        if (keyRef.current === key) setLoading(false);
      }
    })();
  }, [requestKey, brandId, platform, tacticId, postType, take, isUniversal]);

  const save = useCallback(async (templateId: number | null) => {
    if (!brandId) return;
    const key = keyRef.current;
    setError(null);
    const result = await tendApi.setTacticTemplateDefault(
      scopeType,
      isUniversal ? null : brandId,
      platform,
      tacticId,
      postType,
      templateId,
      take,
    );
    if (keyRef.current !== key) return;
    if (result.success && result.resolution) {
      setResolution(result.resolution);
    } else {
      setError(result.error || 'Save failed');
    }
  }, [brandId, scopeType, isUniversal, platform, tacticId, postType, take]);

  // A single-post take previews as a video (the slot it inherits from).
  const placeholderKind = take && ['single', 'post'].includes((postType || 'single').toLowerCase())
    ? 'video'
    : POST_TYPE_PLACEHOLDER[(postType || 'single').toLowerCase()] || 'post';
  const previewContentItem = useMemo(
    () => createPlaceholderContentItem(placeholderKind, previewImageUrl),
    [placeholderKind, previewImageUrl],
  );

  if (!brandId) return null;

  // The scope's own row (what "None" clears) vs. what is in effect.
  const own = isUniversal ? resolution?.global ?? null : resolution?.brand ?? null;
  const effective = resolution?.effective ?? null;
  const template = effective?.slot?.template ?? null;

  let badge: TemplateDefaultSlotBadge | null = null;
  if (effective?.source === 'brand_tactic') {
    badge = { text: 'BRAND', inherited: false };
  } else if (effective?.source === 'global_tactic') {
    badge = isUniversal
      ? { text: 'GLOBAL', inherited: false }
      : { text: 'INHERITED · Universal', inherited: true };
  } else if (effective?.source === 'post_default' && resolution) {
    badge = { text: `INHERITED · ${resolution.fallback_label}`, inherited: true };
  }

  const fallbackLabel = resolution?.fallback_label ?? 'Post default';
  const inheritTarget =
    !isUniversal && resolution?.global?.template
      ? `universal tactic default (${resolution.global.template.name || 'Unnamed'})`
      : resolution?.post_default?.template
        ? `${fallbackLabel} (${resolution.post_default.template.name || 'Unnamed'})`
        : fallbackLabel;

  return (
    <div data-testid="tactic-template-slot" style={{ width }}>
      <TemplateDefaultSlot
        label={label}
        template={template}
        badge={badge}
        emptyHint={loading ? 'Loading…' : !resolution ? 'Could not load' : `Uses ${fallbackLabel} — none set`}
        templates={templates}
        selectedTemplateId={own?.template_id ?? null}
        onSelect={(id) => save(id)}
        noneOption={{ label: `None — use ${inheritTarget}`, onClear: () => save(null) }}
        previewContentItem={previewContentItem}
        brandLogo={brandLogo}
        brandName={brandName}
        previewFillColor={previewFillColor}
        width={width}
        disabled={!canEdit || loading || !resolution}
      />
      {error && <div className="mt-1 text-[10px] text-red-400" role="alert">{error}</div>}
    </div>
  );
};

export default TacticTemplateSlot;
